/**
 * Minimal JWT helpers. We only ever need to read a token's `exp` claim to decide
 * whether a stored session is still usable — the server is the real authority on
 * validity, this is just a cheap client-side gate that avoids firing a request
 * we already know will 401. No signature verification (impossible client-side
 * without the secret) and no dependency: we base64url-decode the payload segment
 * ourselves.
 */

const decodeBase64Url = (segment: string): string | null => {
    const decode =
        typeof atob === 'function'
            ? atob
            : typeof globalThis !== 'undefined' &&
                typeof globalThis.atob === 'function'
              ? globalThis.atob
              : null;
    if (!decode) {
        return null;
    }

    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padLength = (4 - (base64.length % 4)) % 4;
    const padded = base64.padEnd(base64.length + padLength, '=');

    try {
        const binary = decode(padded);
        // Re-interpret the byte string as UTF-8 so non-ASCII claims (e.g. a
        // seller's Georgian name) survive the round-trip intact.
        const utf8 = decodeURIComponent(
            Array.prototype.map
                .call(
                    binary,
                    (char: string) =>
                        '%' +
                        ('00' + char.charCodeAt(0).toString(16)).slice(-2),
                )
                .join(''),
        );
        return utf8;
    } catch {
        return null;
    }
};

/**
 * Reads the `exp` claim (seconds since epoch) from a JWT, or `null` when the
 * token is malformed / has no numeric `exp`.
 */
export const getJwtExp = (token: string): number | null => {
    const parts = token.split('.');
    if (parts.length < 2 || !parts[1]) {
        return null;
    }

    const payloadJson = decodeBase64Url(parts[1]);
    if (!payloadJson) {
        return null;
    }

    try {
        const payload = JSON.parse(payloadJson) as { exp?: unknown };
        return typeof payload.exp === 'number' ? payload.exp : null;
    } catch {
        return null;
    }
};

/**
 * `true` when the token is absent, undecodable, has no `exp`, or its `exp` has
 * already passed. The conservative default (treat anything we can't read as
 * expired) keeps a corrupt token from masquerading as a live session.
 */
export const isJwtExpired = (token: string | null | undefined): boolean => {
    if (!token) {
        return true;
    }
    const exp = getJwtExp(token);
    if (exp === null) {
        return true;
    }
    return Date.now() >= exp * 1000;
};
